/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * YouTube Player Adapter
 *
 * Wraps YT.Player (YouTube IFrame API) with consistent API for unified Player class.
 * All methods return Promises for consistency with Vimeo.
 */
export class YoutubePlayer {
  /**
   * @param {YT.Player} player - Native YouTube player instance
   * @param {Object} options - Player options
   */
  constructor(player, options = {}) {
    this.player = player;
    this.options = options;
  }

  /**
   * Add event listener
   *
   * @param {string} event - Event name
   * @param {Function} listener - Event listener
   * @returns {void}
   */
  on(event, listener) {
    return this.player.addEventListener(event, listener);
  }

  /**
   * Remove event listener
   *
   * @param {string} event - Event name
   * @param {Function} [listener] - Event listener
   * @returns {void}
   */
  off(event, listener) {
    return this.player.removeEventListener(event, listener);
  }

  /**
   * Load new video by ID
   *
   * @param {string|number} id - Video ID
   * @returns {Promise<void>}
   */
  async loadVideo(id) {
    await this.stop();
    await Promise.resolve(this.player.loadPlaylist(id));
    await this.setLoop(!!this.options.loop);
  }

  /**
   * Check if playing
   *
   * @returns {Promise<boolean>}
   */
  async isPlaying() {
    return Promise.resolve(
      this.player.getPlayerState() === window.YT.PlayerState.PLAYING
    );
  }

  /**
   * Check if paused
   *
   * @returns {Promise<boolean>}
   */
  async isPaused() {
    return Promise.resolve(
      this.player.getPlayerState() === window.YT.PlayerState.PAUSED
    );
  }

  /**
   * Check if ended
   *
   * @returns {Promise<boolean>}
   */
  async isEnded() {
    return Promise.resolve(
      this.player.getPlayerState() === window.YT.PlayerState.ENDED
    );
  }

  /**
   * Set autopause (not supported by YouTube)
   *
   * @param {boolean} autopause - Enable autopause
   * @returns {Promise<void>}
   */
  async setAutopause(autopause) {
    return Promise.resolve(null);
  }

  /**
   * Set color (not supported by YouTube)
   *
   * @param {string} color - Hex or rgb color
   * @returns {Promise<void>}
   */
  async setColor(color) {
    return Promise.resolve(null);
  }

  /**
   * Set loop
   *
   * @param {boolean} loop - Enable loop
   * @returns {Promise<void>}
   */
  async setLoop(loop) {
    return Promise.resolve(this.player.setLoop(loop));
  }

  /**
   * Check if muted
   *
   * @returns {Promise<boolean>}
   */
  async isMuted() {
    return Promise.resolve(this.player.isMuted());
  }

  /**
   * Set muted state
   *
   * @param {boolean} muted - Mute or unmute
   * @returns {Promise<void>}
   */
  async setMuted(muted) {
    return muted ? this.mute() : this.unMute();
  }

  /**
   * Set playback rate
   *
   * @param {number} rate - Playback rate (0.25, 0.5, 1, 1.5, 2)
   * @returns {Promise<void>}
   */
  async setPlaybackRate(rate) {
    return Promise.resolve(
      this.player.setPlaybackRate(rate > 10 ? rate / 100 : rate)
    );
  }

  /**
   * Set volume
   *
   * @param {number} level - Volume level (0 to 100)
   * @returns {Promise<void>}
   */
  async setVolume(level) {
    // Accepts 0-100
    return Promise.resolve(
      this.player.setVolume(level > 1 ? level : level * 100)
    );
  }

  /**
   * Unload player (not supported by YouTube)
   *
   * @returns {Promise<void>}
   */
  async unload() {
    return Promise.resolve(null);
  }

  /**
   * Destroy player
   *
   * @returns {Promise<void>}
   */
  async destroy() {
    return Promise.resolve(this.player.destroy());
  }

  /**
   * Mute video
   *
   * @returns {Promise<void>}
   */
  async mute() {
    return Promise.resolve(this.player.mute());
  }

  /**
   * Unmute video
   *
   * @returns {Promise<void>}
   */
  async unMute() {
    return Promise.resolve(this.player.unMute());
  }

  /**
   * Play video
   *
   * @returns {Promise<void>}
   */
  async play() {
    return Promise.resolve(this.player.playVideo());
  }

  /**
   * Pause video
   *
   * @returns {Promise<void>}
   */
  async pause() {
    return Promise.resolve(this.player.pauseVideo());
  }

  /**
   * Stop video
   *
   * @returns {Promise<void>}
   */
  async stop() {
    return Promise.resolve(this.player.stopVideo());
  }

  /**
   * Call method directly on player
   *
   * @param {string} method - Method name
   * @param {...any} args - Method arguments
   * @returns {Promise<any>}
   */
  async call(method, ...args) {
    const result = this.player[method](...args);

    return result instanceof Promise ? result : Promise.resolve(result);
  }
}
